import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  IconButton,
  SelectChangeEvent,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { SAPIconButton, PageLayout, FilterBar } from '../components';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Visibility as ViewIcon, Close as CloseIcon } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { useAuth } from '../api/auth/authContext';
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchJobDetails } from '../api/features/jobDetail/jobDetailApi';
import { 
  fetchStepHistory, 
  fetchStepHistoryCount, 
  fetchStepHistoryEnums 
} from '../api/features/stepHistory/stephistoryApi';
import { epochToDateTime } from '../api/utils';
import type { StepHistoryData } from '../api/features/stepHistory/stephistory.types';

interface ClientOption {
  label: string;
  value: number;
  code: string;
}

interface JobOption {
  label: string;
  value: number;
  jobName: string;
}

// Constants
const ROWS_PER_PAGE = 10;

// SAP Styled Components (aligned with Job Run / Integration)
const SAPFilterCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
  marginBottom: SAP_SPACING.xl,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.sm,
}));

const SAPTableHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  color: SAP_COLORS.sapGray7,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
  borderBottom: `2px solid ${SAP_COLORS.sapGray3}`,
  fontSize: '0.875rem',
}));


export default function StepHistory() {
  const { clientCode } = useClientCode();
  const { isAuthenticated } = useAuth();
  const isAdmin = clientCode === 'admin';

  // Main data state
  const [stepHistory, setStepHistory] = useState<StepHistoryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  // Filter states
  const [stepId, setStepId] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobOption | null>(null);
  const [request, setRequest] = useState<string>('');
  const [response, setResponse] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [lastUpdateDate, setLastUpdateDate] = useState<Date | null>(null);

  // Dropdown options
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Detail view state
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<StepHistoryData | null>(null);
  const [detailTab, setDetailTab] = useState<'request' | 'response' | 'config' | 'header'>('request');

  // Load client options on component mount
  useEffect(() => {
    if (isAdmin) {
      fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
        .then((clients) => {
          setClientOptions(clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code })));
        })
        .catch(() => setClientOptions([]));
    }
  }, [clientCode, isAdmin]);

  // Load job options when client is selected
  useEffect(() => {
    if (selectedClient && selectedClient.value) {
      fetchJobDetails({ clientCode, clientId: selectedClient.value, firstResult: -1, maxResult: 0 })
        .then((jobs) => {
          setJobOptions(jobs.map((job: any) => ({ 
            label: job[2] || 'Unknown Job', 
            value: job[0], // jobId is at index 0
            jobName: job[2] 
          })));
        })
        .catch(() => setJobOptions([]));
      setSelectedJob(null);
    } else {
      setJobOptions([]);
      setSelectedJob(null);
    }
  }, [selectedClient, clientCode]);

  // Load status enum options
  useEffect(() => {
    setLoadingOptions(true);
    fetchStepHistoryEnums({ clientCode })
      .then((enums) => {
        setStatusOptions(enums);
      })
      .catch(() => {
        setStatusOptions([]);
      })
      .finally(() => {
        setLoadingOptions(false);
      });
  }, [clientCode]);

  // Load step history data
  const loadStepHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: any = {
        clientCode,
        firstResult: page * ROWS_PER_PAGE,
        maxResult: ROWS_PER_PAGE,
        searched: true,
      };

      if (stepId && stepId.trim() !== '') {
        params.stepId = stepId.trim();
      }
      if (selectedClient?.value) {
        params.clientId = selectedClient.value;
      }
      if (selectedJob?.value) {
        params.jobId = selectedJob.value;
      }
      if (request && request.trim() !== '') {
        params.request = request.trim();
      }
      if (response && response.trim() !== '') {
        params.response = response.trim();
      }
      if (status && status !== '') {
        params.status = status;
      }
      if (lastUpdateDate) {
        // Format date/time for API (same pattern as integration failure)
        const dateObj = lastUpdateDate;
        params.lastUpdateDate = dateObj.toString();
        // Format as DD/MM/YYYY for lastUpdateDateValue
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        params.lastUpdateDateValue = `${day}/${month}/${year}`;
      }

      // Create count params without pagination
      const countParams: any = {
        clientCode,
        searched: true,
      };

      if (stepId && stepId.trim() !== '') {
        countParams.stepId = stepId.trim();
      }
      if (selectedClient?.value) {
        countParams.clientId = selectedClient.value;
      }
      if (selectedJob?.value) {
        countParams.jobId = selectedJob.value;
      }
      if (request && request.trim() !== '') {
        countParams.request = request.trim();
      }
      if (response && response.trim() !== '') {
        countParams.response = response.trim();
      }
      if (status && status !== '') {
        countParams.status = status;
      }
      if (lastUpdateDate) {
        const dateObj = lastUpdateDate;
        countParams.lastUpdateDate = dateObj.toString();
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        countParams.lastUpdateDateValue = `${day}/${month}/${year}`;
      }

      const [data, count] = await Promise.all([
        fetchStepHistory(params),
        fetchStepHistoryCount(countParams),
      ]);

      setStepHistory(data);
      setTotalCount(count);
    } catch (err: any) {
      setError('Failed to load step history');
      setStepHistory([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
    loadStepHistory();
  };

  // Handle reset
  const handleReset = () => {
    setStepId('');
    setSelectedClient(null);
    setSelectedJob(null);
    setRequest('');
    setResponse('');
    setStatus('');
    setLastUpdateDate(null);
    setPage(0);
    setHasSearched(false);
    setStepHistory([]);
    setTotalCount(0);
    setError(null);
  };

  // Handle page change
  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage - 1);
  };

  // Reload data when page changes
  useEffect(() => {
    if (hasSearched) {
      loadStepHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Format date for display (with time, like IntegrationInstanceFailure)
  const formatDate = (timestamp: number): string => {
    if (!timestamp) return 'N/A';
    return epochToDateTime(timestamp, true);
  };

  // Truncate long text
  const truncateText = (text: string, maxLength: number = 50): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatJson = (value: string | null | undefined): string => {
    if (!value || value === 'null') return 'N/A';
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  };

  const handleViewDetails = (history: StepHistoryData) => {
    setSelectedHistory(history);
    setDetailTab('request');
    setDetailOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailOpen(false);
    setSelectedHistory(null);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageLayout
        title="Step History"
        description="Search and view step execution history with detailed request and response data"
      >
        <FilterBar loading={loading}>
          <Box sx={{ width: '100%' }}>
            {/* Row 1 - Entity Filters: Step ID, Client, Job */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Step ID */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                  <TextField
                    label="Step ID"
                    value={stepId}
                    onChange={(e) => setStepId(e.target.value)}
                    placeholder="Enter step ID"
                    fullWidth
                  />
                </Box>

                {/* Client Dropdown */}
                {isAdmin && (
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                    <Autocomplete
                      options={clientOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(opt, val) => String(opt.value) === String(val.value)}
                      value={selectedClient}
                      onChange={(_, newValue) => setSelectedClient(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} label="Client Name" />
                      )}
                    />
                  </Box>
                )}

                {/* Job Name Dropdown */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                  <Autocomplete
                    options={jobOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(opt, val) => String(opt.value) === String(val.value)}
                    value={selectedJob}
                    onChange={(_, newValue) => setSelectedJob(newValue)}
                    disabled={!selectedClient && isAdmin}
                    renderInput={(params) => (
                      <TextField {...params} label="Job Name" />
                    )}
                  />
                </Box>
              </Box>
            </Box>

            {/* Row 2 - Search Filters: Request, Response, Status */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Request Input */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                  <TextField
                    label="Request"
                    value={request}
                    onChange={(e) => setRequest(e.target.value)}
                    placeholder="Enter request search criteria"
                    fullWidth
                  />
                </Box>

                {/* Response Input */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                  <TextField
                    label="Response"
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Enter response search criteria"
                    fullWidth
                  />
                </Box>

                {/* Status Dropdown */}
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(33.333% - 11px)' } }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={status}
                      label="Status"
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {statusOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {/* Row 3 - Date Range: Last Update Date & Time */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Last Update Date & Time */}
                <Box sx={{ 
                  flex: { xs: '1 1 100%', sm: '0 1 auto', md: '0 1 auto' },
                  minWidth: { xs: '100%', sm: '280px', md: '320px' },
                  maxWidth: { xs: '100%', sm: '400px', md: '450px' }
                }}>
                  <DateTimePicker
                    label="Last Update Date & Time"
                    value={lastUpdateDate}
                    onChange={(newValue) => setLastUpdateDate(newValue)}
                    ampm={false}
                    views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                    format="dd/MM/yyyy HH:mm:ss"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Row 4 - Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: SAP_SPACING.md,
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                alignItems: 'center',
                paddingTop: SAP_SPACING.md,
                borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
              }}
            >
              <SAPIconButton
                icon="search"
                onClick={handleSearch}
                variant="primary"
                loading={loading}
                sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
              />
              <SAPIconButton
                icon="reset"
                onClick={handleReset}
                variant="secondary"
                sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
              />
            </Box>
          </Box>
        </FilterBar>

        {/* Data Section */}
        <SAPDataCard>
          <CardContent sx={{ p: 0 }}>
            {hasSearched ? (
              <>
                {/* Loading State */}
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                )}

                {/* Error State */}
                {error && (
                  <Alert severity="error" sx={{ m: 2 }}>
                    {typeof error === 'string' ? error : (error as any)?.message || 'Something went wrong'}
                  </Alert>
                )}

                {/* Data Display */}
                {!loading && !error && (
                  <>
                    <TableContainer 
                      component={Paper} 
                      sx={{ 
                        boxShadow: 'none', 
                        backgroundColor: SAP_COLORS.sapWhite 
                      }}
                    >
                      <Table sx={{ backgroundColor: SAP_COLORS.sapWhite }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: SAP_COLORS.sapLightBlue }}>
                            <SAPTableHeader>S.No.</SAPTableHeader>
                            <SAPTableHeader>Step ID</SAPTableHeader>
                            <SAPTableHeader>Date</SAPTableHeader>
                            <SAPTableHeader>Response</SAPTableHeader>
                            <SAPTableHeader align="center">Status</SAPTableHeader>
                            <SAPTableHeader align="center">View</SAPTableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: SAP_COLORS.sapWhite }}>
                          {stepHistory.map((history, index) => (
                            <TableRow 
                              key={history.stepHistoryId} 
                              hover 
                              sx={{ 
                                backgroundColor: index % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                                '&:hover': { 
                                  backgroundColor: SAP_COLORS.hover,
                                  cursor: 'pointer',
                                },
                              }}
                            >
                              <TableCell sx={{ 
                                color: SAP_COLORS.sapGray6,
                                borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                              }}>
                                {page * ROWS_PER_PAGE + index + 1}
                              </TableCell>
                              <TableCell sx={{ 
                                borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                              }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                    color: SAP_COLORS.sapGray7,
                                  }}
                                >
                                  {history.stepId}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ 
                                borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                              }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{
                                    color: SAP_COLORS.sapGray6,
                                    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                  }}
                                >
                                  {formatDate(history.date)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ 
                                borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                                maxWidth: '40%',
                                wordBreak: 'break-word',
                              }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: SAP_COLORS.sapGray6,
                                    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {history.response || 'No response'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ 
                                borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                              }} align="center">
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: 
                                      history.status === 'SUCCESS' 
                                        ? SAP_COLORS.sapLightGreen 
                                        : SAP_COLORS.sapLightRed,
                                    color: 
                                      history.status === 'SUCCESS' 
                                        ? SAP_COLORS.sapGreen 
                                        : SAP_COLORS.sapRed,
                                    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {history.status}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ 
                                borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                              }} align="center">
                                <SAPIconButton
                                  icon="view"
                                  onClick={() => handleViewDetails(history)}
                                  variant="secondary"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {totalCount > 0 && (
                      <Box sx={{ mt: 2, px: 2, pb: 2 }}>
                        {/* Pagination Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + stepHistory.length, totalCount)} of {totalCount} step histories
                          </Typography>
                        </Box>
                        
                        {/* Pagination Controls */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Rows per page</InputLabel>
                            <Select
                              value={ROWS_PER_PAGE}
                              onChange={(event: SelectChangeEvent<number>) => {
                                // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                              }}
                              label="Rows per page"
                            >
                              <MenuItem value={5}>5</MenuItem>
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={25}>25</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                            </Select>
                          </FormControl>
                          
                          <Pagination
                            count={Math.ceil(totalCount / ROWS_PER_PAGE)}
                            page={page + 1}
                            onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                              handlePageChange(event, newPage - 1);
                            }}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      </Box>
                    )}
                  </>
                )}

                {/* Empty State */}
                {!loading && !error && stepHistory.length === 0 && (
                  <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No step history found. Try searching with different criteria.
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              // Initial Empty State
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No step history found. Try searching with different criteria.
                </Typography>
              </Box>
            )}
          </CardContent>
        </SAPDataCard>

      {/* Detail View Modal - Same UI as Step History Elastic: summary row + Request/Response/Config/Header tabs */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.32)' }
        }}
        PaperProps={{
          sx: {
            bgcolor: SAP_COLORS.sapWhite,
            boxShadow: SAP_SHADOWS.lg,
            borderRadius: 2,
            overflow: 'hidden',
            minHeight: 520,
            minWidth: { xs: '95vw', sm: 560 },
            maxHeight: '90vh',
            m: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: SAP_COLORS.sapGray1, 
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`, 
            px: { xs: SAP_SPACING.md, sm: SAP_SPACING.lg },
            py: SAP_SPACING.md,
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: SAP_TYPOGRAPHY.fontWeight.bold, 
                color: SAP_COLORS.sapBlue,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              Step History Details
            </Typography>
            <IconButton 
              onClick={handleCloseDetails}
              sx={{ 
                color: SAP_COLORS.sapGray6,
                p: SAP_SPACING.sm,
                '&:hover': { 
                  bgcolor: SAP_COLORS.hover,
                  color: SAP_COLORS.sapGray7
                }
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            p: { xs: SAP_SPACING.md, sm: SAP_SPACING.lg }, 
            bgcolor: SAP_COLORS.sapWhite,
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '10px'
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: SAP_COLORS.sapGray1,
              borderRadius: '5px'
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: SAP_COLORS.sapGray3,
              borderRadius: '5px',
              '&:hover': {
                bgcolor: SAP_COLORS.sapGray4
              }
            }
          }}
        >
          {selectedHistory ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: SAP_SPACING.lg }}>
              {/* Meta row: Step, Date, Status — spaced equally */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  width: '100%',
                  gap: SAP_SPACING.md,
                  mb: SAP_SPACING.sm,
                }}
              >
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: SAP_COLORS.sapGray5,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Step
                  </Typography>
                  <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
                    {selectedHistory.stepId ?? 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: SAP_COLORS.sapGray5,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Date
                  </Typography>
                  <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
                    {formatDate(selectedHistory.date)}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: SAP_COLORS.sapGray5,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Status
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      px: 2,
                      py: 0.5,
                      borderRadius: 999,
                      backgroundColor:
                        selectedHistory.status === 'SUCCESS'
                          ? SAP_COLORS.sapLightGreen
                          : SAP_COLORS.sapLightRed,
                      color:
                        selectedHistory.status === 'SUCCESS'
                          ? SAP_COLORS.sapGreen
                          : SAP_COLORS.sapRed,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                      fontSize: '0.875rem',
                      minWidth: 80,
                      textAlign: 'center',
                    }}
                  >
                    {selectedHistory.status || 'N/A'}
                  </Box>
                </Box>
              </Box>

              {/* Tabs for Request / Response / Config / Header */}
              <Tabs
                value={detailTab}
                onChange={(_, v) => setDetailTab(v)}
                textColor="primary"
                indicatorColor="primary"
                sx={{ borderBottom: `1px solid ${SAP_COLORS.sapGray2}`, mb: SAP_SPACING.sm }}
              >
                <Tab label="Request" value="request" />
                <Tab label="Response" value="response" />
                <Tab label="Config" value="config" />
                <Tab label="Header" value="header" />
              </Tabs>

              {/* Single content panel for active tab — fills modal width/height */}
              <Box
                sx={{
                  bgcolor: SAP_COLORS.sapWhite,
                  p: SAP_SPACING.md,
                  borderRadius: 1,
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  border: `1px solid ${SAP_COLORS.sapGray2}`,
                  width: '100%',
                  minHeight: 420,
                  maxHeight: 'min(65vh, 560px)',
                  overflow: 'auto',
                  wordBreak: 'break-word',
                  lineHeight: 1.6,
                  color: SAP_COLORS.sapGray7,
                }}
              >
                {detailTab === 'request' && (
                  selectedHistory.request ? (
                    formatJson(selectedHistory.request)
                  ) : (
                    <Typography sx={{ color: SAP_COLORS.sapGray5, fontStyle: 'italic' }}>
                      No request data available
                    </Typography>
                  )
                )}
                {detailTab === 'response' && (
                  selectedHistory.response ? (
                    formatJson(selectedHistory.response)
                  ) : (
                    <Typography sx={{ color: SAP_COLORS.sapGray5, fontStyle: 'italic' }}>
                      No response data available
                    </Typography>
                  )
                )}
                {detailTab === 'config' && (
                  (selectedHistory.configObjectJson != null && String(selectedHistory.configObjectJson).trim() !== '') ? (
                    formatJson(String(selectedHistory.configObjectJson))
                  ) : (
                    <Typography sx={{ color: SAP_COLORS.sapGray5, fontStyle: 'italic' }}>
                      No config object json data available
                    </Typography>
                  )
                )}
                {detailTab === 'header' && (
                  selectedHistory.header ? (
                    formatJson(selectedHistory.header)
                  ) : (
                    <Typography sx={{ color: SAP_COLORS.sapGray5, fontStyle: 'italic' }}>
                      No header data available
                    </Typography>
                  )
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5 }}>
                No details available
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      </PageLayout>
    </LocalizationProvider>
  );
}